/**
 * @copyright 2024-2026 David Baugh
 * @license Proprietary - All Rights Reserved
 * Unauthorized copying, modification, or distribution is strictly prohibited.
 */

// analytics.js - Google Analytics utility service for Safety Testing App
// Integrates with existing logger.js to provide comprehensive analytics

class AnalyticsService {
  constructor() {
    this.isEnabled = false;
    // DEMO SITE: hardcoded demo tracking ID — ignore env var and admin settings
    // so demo engagement always reports to the demo property.
    this.trackingId = 'G-2FN8MWJEEB';
    this.initialize();
  }

  initialize() {
    console.log('🔍 Analytics Service Initializing...', {
      trackingId: this.trackingId,
      hasWindow: typeof window !== 'undefined',
      hasGtag: typeof window !== 'undefined' && !!window.gtag,
      hasDataLayer: typeof window !== 'undefined' && !!window.dataLayer,
      environment: process.env.NODE_ENV
    });

    if (this.trackingId && typeof window !== 'undefined' && window.gtag) {
      this.isEnabled = true;
      console.log('✅ Google Analytics initialized successfully:', this.trackingId);
      
      // Test initial configuration
      this.gtag('config', this.trackingId, {
        debug_mode: process.env.NODE_ENV === 'development',
        send_page_view: true
      });
    } else {
      console.warn('❌ Google Analytics initialization failed:', {
        trackingId: this.trackingId,
        hasWindow: typeof window !== 'undefined',
        hasGtag: typeof window !== 'undefined' && !!window.gtag
      });
    }
  }

  // Safe wrapper for gtag calls
  gtag(...args) {
    if (this.isEnabled && window.gtag) {
      console.log('📊 GA Event:', args[0], args[1], args[2] ? Object.keys(args[2]).length + ' params' : 'no params');
      window.gtag(...args);
    } else {
      console.warn('⚠️ GA not available, skipping event:', args[0], args[1]);
    }
  }

  // Page view tracking
  trackPageView(path, title = null) {
    this.gtag('config', this.trackingId, {
      page_path: path,
      page_title: title,
    });
  }

  // Custom event tracking
  trackEvent(action, parameters = {}) {
    this.gtag('event', action, {
      event_category: parameters.category || 'general',
      event_label: parameters.label,
      value: parameters.value,
      ...parameters
    });
  }

  // User properties for educational context
  setUserProperties(userId, role, cohortId = null) {
    this.gtag('config', this.trackingId, {
      custom_map: {
        custom_parameter_1: 'user_role',
        custom_parameter_2: 'cohort_id'
      }
    });

    this.gtag('set', {
      user_id: userId ? `user_${userId}` : undefined, // Hash/prefix for privacy
      user_role: role,
      cohort_id: cohortId ? `cohort_${cohortId}` : null,
    });
  }

  // Authentication events
  trackLogin(userId, role, method = 'pin') {
    this.trackEvent('login', {
      category: 'authentication',
      method: method,
      user_role: role,
      label: `${role}_login`
    });
  }

  trackLogout(userId, role, sessionDuration = null) {
    this.trackEvent('logout', {
      category: 'authentication',
      user_role: role,
      value: sessionDuration,
      label: `${role}_logout`
    });
  }

  trackRegistration(role) {
    this.trackEvent('sign_up', {
      category: 'authentication',
      method: 'pin_registration',
      user_role: role
    });
  }

  trackRoleSwitch(fromRole, toRole) {
    this.trackEvent('role_switch', {
      category: 'navigation',
      from_role: fromRole,
      to_role: toRole,
      label: `${fromRole}_to_${toRole}`
    });
  }

  // Test-specific events for educational insights
  trackTestStart(testId, testName, userId, timeLimit = null) {
    this.trackEvent('test_start', {
      category: 'education',
      test_id: testId,
      test_name: testName,
      time_limit: timeLimit,
      label: testName
    });
  }

  trackTestComplete(testId, testName, score, total, percentage, passed, timeSpent) {
    this.trackEvent('test_complete', {
      category: 'education',
      test_id: testId,
      test_name: testName,
      score: score,
      total_questions: total,
      percentage: Math.round(percentage),
      passed: passed ? 1 : 0,
      time_spent: Math.round(timeSpent / 1000), // Convert to seconds
      value: Math.round(percentage), // Use percentage as value for sorting
      label: `${testName}_${passed ? 'pass' : 'fail'}`
    });
  }

  trackTestAbandon(testId, testName, questionsAnswered, timeSpent) {
    this.trackEvent('test_abandon', {
      category: 'education',
      test_id: testId,
      test_name: testName,
      questions_answered: questionsAnswered,
      time_spent: Math.round(timeSpent / 1000),
      label: testName
    });
  }

  trackQuestionView(testId, questionId, questionType, hasImage = false) {
    this.trackEvent('question_view', {
      category: 'education',
      test_id: testId,
      question_id: questionId,
      question_type: questionType,
      has_image: hasImage ? 1 : 0
    });
  }

  trackQuestionAnswer(testId, questionId, isCorrect, timeTaken = null) {
    this.trackEvent('question_answer', {
      category: 'education',
      test_id: testId,
      question_id: questionId,
      correct: isCorrect ? 1 : 0,
      time_taken: timeTaken ? Math.round(timeTaken / 1000) : null,
      value: isCorrect ? 1 : 0
    });
  }

  // Failed question review tracking
  trackFailedQuestionReview(testId, questionId) {
    this.trackEvent('failed_question_review', {
      category: 'education',
      test_id: testId,
      question_id: questionId
    });
  }

  // Admin/Teacher specific events
  trackTestCreation(testId, testName, creatorRole, questionCount) {
    this.trackEvent('test_create', {
      category: 'content_management',
      test_id: testId,
      test_name: testName,
      creator_role: creatorRole,
      question_count: questionCount,
      label: testName
    });
  }

  trackTestAssignment(testId, testName, cohortId, assignedBy) {
    this.trackEvent('test_assign', {
      category: 'content_management',
      test_id: testId,
      test_name: testName,
      cohort_id: cohortId,
      assigned_by_role: assignedBy,
      label: testName
    });
  }

  trackUserManagement(action, targetRole, adminRole) {
    this.trackEvent('user_management', {
      category: 'administration',
      action: action, // create, delete, modify
      target_role: targetRole,
      admin_role: adminRole,
      label: `${action}_${targetRole}`
    });
  }

  // System performance events
  trackApiResponse(endpoint, method, statusCode, responseTime) {
    this.trackEvent('api_response', {
      category: 'performance',
      endpoint: endpoint,
      method: method,
      status_code: statusCode,
      response_time: Math.round(responseTime),
      value: Math.round(responseTime)
    });
  }

  trackError(errorType, errorMessage, component = null, userId = null) {
    this.trackEvent('error', {
      category: 'errors',
      error_type: errorType,
      error_message: errorMessage.substring(0, 100), // Limit length
      component: component,
      fatal: false
    });
  }

  // Engagement metrics
  trackImageInteraction(imageId, action = 'view') {
    this.trackEvent('image_interaction', {
      category: 'engagement',
      image_id: imageId,
      action: action // view, zoom, etc.
    });
  }

  // Educational analytics helpers
  trackLearningPath(fromTest, toTest, userId) {
    this.trackEvent('learning_progression', {
      category: 'education',
      from_test: fromTest,
      to_test: toTest,
      label: `${fromTest}_to_${toTest}`
    });
  }

  // Batch event for test session analysis
  trackTestSession(sessionData) {
    this.trackEvent('test_session', {
      category: 'education',
      ...sessionData,
      label: sessionData.test_name
    });
  }
}

// Create singleton instance
const analytics = new AnalyticsService();

export default analytics;